import { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';

import {
  addReview,
  updateReview,
  addReviewForCookbook,
  updateReviewForCookbook,
} from 'actions/reviews';
import CheckboxCard from 'components/common/CheckboxCard';
import ErrorMessage from 'components/common/ErrorMessage';
import BooleanInputGroup from 'components/common/forms/BooleanInputGroup';
import BodyModal from 'components/common/modals/BodyModal';
import StarRating from 'components/common/StarRating';
import CloseX from 'components/icons/navigation/CloseX';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import {
  HIGH_RATING,
  LOW_RATING,
  HIGH_RATING_MESSAGE,
  LOW_RATING_MESSAGE,
  REVIEW_REASON_PREFIX,
} from 'lib/constants';
import * as fromDashboard from 'reducers/dashboard';
import * as Types from 'utilities/types';

const TempoTaggedReviewModal = ({
  mealSelectionId = '',
  onShowToast = () => {},
  isOpen,
  onClose,
  mealId,
  menuId,
  existingReview,
  onAddReview,
  onAddReviewForCookbook,
  onUpdateReview,
  onUpdateReviewForCookbook,
  photoSrc,
  reviewReasons,
  title,
  ratingSubmittedEvent,
  reviewSubmittedEvent,
}) => {
  const [rating, setRating] = useState(null);
  const [editedComment, setEditedComment] = useState(null);
  const [error, setError] = useState(null);
  const [responseSelections, setResponseSelections] = useState([]);
  const [initialSelectionCount, setInitialSelectionCount] = useState(0);
  const closeRef = useRef();
  const onUpdateMethod = mealSelectionId ? onUpdateReviewForCookbook : onUpdateReview;
  const onAddMethod = mealSelectionId ? onAddReviewForCookbook : onAddReview;
  const { recipeRating, comment: existingComment, pendingChanges } = existingReview || {};

  useEffect(() => {
    if (isOpen) {
      if (recipeRating !== null) {
        setExistingReview();
      } else if (pendingChanges?.recipeRating && rating) {
        setPendingChanges();
      }
    }
  }, [isOpen, existingReview]);

  useEffect(() => {
    if (initialSelectionCount !== responseSelections.length) {
      saveRating(rating, true);
    }
  }, [responseSelections, initialSelectionCount]);

  const onSubmitReview = useAnalyticsCallback(
    () => handleSubmit(),
    ({ logEvent }) => {
      if (reviewSubmittedEvent) {
        logEvent(reviewSubmittedEvent, {
          mealTitle: title,
          review: formatCommentAndReasons(editedComment),
        });
      }
    }
  );

  const handleRatingChange = newRating => {
    if (newRating === undefined || newRating === null) return;

    const ratingValue = parseInt(newRating, 10);
    if (isNaN(ratingValue)) return;

    setRating(ratingValue);
    saveRating(ratingValue, true);
  };

  const setExistingReview = () => {
    setRating(recipeRating);
    if (existingReview?.comment) {
      const selectedReviewReasons = fetchSelectedReviewReasons(existingComment);
      setResponseSelections(selectedReviewReasons);
      setInitialSelectionCount(selectedReviewReasons.length);

      if (editedComment === null) {
        setEditedComment(removeReviewReasons(existingReview.comment));
      }
    }
  };

  const setPendingChanges = () => {
    setRating(pendingChanges.recipeRating);
    saveRating(pendingChanges.recipeRating);
  };

  const fetchSelectedReviewReasons = comment => {
    if (comment === null) return [];

    const reasonsPart = comment.split(REVIEW_REASON_PREFIX)[1];
    if (!reasonsPart) return [];

    const foundReasons = reasonsPart
      .split(',')
      .map(reason => reason.trim())
      .filter(reason => reviewReasons?.includes(reason));

    return foundReasons;
  };

  const removeReviewReasons = comment => {
    if (comment === null) return;

    const index = comment.indexOf(REVIEW_REASON_PREFIX);
    if (index === -1) return comment;

    return comment.substring(0, index).trim();
  };

  const saveRating = async (ratingToSave, keepCurrentComment = false) => {
    if (ratingToSave === null) {
      return;
    }

    const commentToFormat = keepCurrentComment
      ? removeReviewReasons(existingReview.comment)
      : editedComment;

    try {
      if (existingReview?.id) {
        await onUpdateMethod(mealSelectionId || existingReview.id, {
          recipeRating: ratingToSave,
          comment: formatCommentAndReasons(commentToFormat),
          mealId,
          menuId,
        });
      } else {
        await onAddMethod({
          recipeRating: ratingToSave,
          comment: formatCommentAndReasons(commentToFormat),
          mealId,
          menuId,
        });
      }
    } catch {
      setError('Unable to save your rating. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (!rating) return;

    try {
      if (existingReview?.id) {
        await onUpdateMethod(mealSelectionId || existingReview.id, {
          recipeRating: rating,
          comment: formatCommentAndReasons(editedComment),
          mealId,
          menuId,
        });
      } else {
        await onAddMethod({
          recipeRating: rating,
          comment: formatCommentAndReasons(editedComment),
          mealId,
          menuId,
        });
      }
      onClose();
      onShowToast();
    } catch {
      setError('Unable to submit your review. Please try again.');
    }
  };

  const getRatingMessage = rating => {
    if (rating >= HIGH_RATING) {
      return HIGH_RATING_MESSAGE;
    }
    if (rating <= LOW_RATING) {
      return LOW_RATING_MESSAGE;
    }
    return null;
  };

  const addResponseSelection = option => {
    setResponseSelections(prevSelections => {
      const selections = prevSelections ?? [];

      if (selections.includes(option)) {
        return selections.filter(item => item !== option);
      }

      return [...selections, option];
    });
  };

  const formatCommentAndReasons = comment => {
    let reviewReasonString = null;
    const reasons = [];

    if (responseSelections.length > 0) {
      const selectionString = responseSelections.join(',');
      reviewReasonString = `${REVIEW_REASON_PREFIX} ${selectionString}`;
    }

    if (comment != null) reasons.push(comment);
    if (reviewReasonString != null) reasons.push(reviewReasonString);

    return reasons.join('');
  };

  if (!isOpen) return null;

  return (
    <BodyModal
      onDismiss={onClose}
      topBarStyle="hidden"
      titleStyle="hidden"
      bodyPadding={'p-0'}
      containerStyle="w-full max-w-[432px]"
      contentClassname="rounded-lg overflow-hidden"
      bodyStyle="h-full overflow-auto"
      isMinHeight={true}
    >
      <div className="relative mx-auto flex flex-col">
        <div className="top-4 flex w-full justify-end">
          <button
            ref={closeRef}
            className="fixed mr-4 mt-4 h-8 w-8 rounded-[32px] bg-white-100 bg-opacity-40 p-1 text-charcoal-90"
            onClick={onClose}
          >
            <span className="sr-only">Close review modal for {title}</span>
            <CloseX className="h-6 w-6" />
          </button>
        </div>

        <img className={'block !h-[150px] w-full object-cover'} alt={title} src={photoSrc} />
        <div className="flex flex-col p-4">
          {error && <ErrorMessage message={error} />}
          <div className="text-left">
            <h2 className="text text-charcoal-90">How was your meal?</h2>
            <span className="textMd">{title}</span>
          </div>

          <div className="justify-left flex pb-4 pt-2">
            <StarRating
              uniqueId={`meal-review-${mealId}`}
              onChange={handleRatingChange}
              rating={rating}
              disabled={false}
              starSvgClassName="h-[29px] w-[29px]"
              customOutlineColor="text-charcoal-100"
              ratingSubmittedEvent={ratingSubmittedEvent}
            />
          </div>

          <div className="pb-2">
            {getRatingMessage(rating) && (
              <>
                <p className="textMd text-left text-charcoal-100">{getRatingMessage(rating)}</p>
                <p className="text text-charcoal-90">Please select all that apply.</p>
              </>
            )}
          </div>

          <div className="mb-1">
            <BooleanInputGroup>
              <div
                className="mb-4 mt-2 flex flex-wrap gap-x-1 gap-y-1.5"
                role="radiogroup"
                aria-label="Servings options"
              >
                {reviewReasons.map(option => (
                  <CheckboxCard
                    key={option}
                    checked={responseSelections?.includes(option)}
                    onClick={() => addResponseSelection(option)}
                    styleRefresh={true}
                    labelClassName="py-3 px-4 m-px"
                    selectedStandardBorderClassName="border-green-100 mx-0 my-0"
                    selectedTempoTextClassName="text-tempo-navy-100"
                    selectedTempoBorderClassName="border-tempo-navy-90 mx-0 my-0"
                    textClassName="text-tempo-black-100"
                    borderClassName="border-tempo-black-100"
                    item={{
                      ...option,
                      name: option,
                      displayName: option,
                    }}
                    inputName="preferences"
                    iconComponent={null}
                  />
                ))}
              </div>
            </BooleanInputGroup>
          </div>
          <div className="mb-2 text-left">
            <p className="textMd text-charcoal-100">Please share more about what you selected.</p>
            <p className="text text-charcoal-90">Our team reads every review!</p>
          </div>
          <textarea
            value={editedComment === null ? '' : editedComment}
            onChange={e => setEditedComment(e.target.value)}
            className="mb-4 w-full flex-grow resize-none rounded border border-charcoal-80 bg-white-100 p-4 shadow-none placeholder:text-charcoal-90 focus:outline-none focus:placeholder:text-transparent"
            rows="4"
          />
          <button
            onClick={onSubmitReview}
            className="textMd btn btnPrimary sticky bottom-4 w-full rounded py-4 text-white-100 transition-colors"
          >
            Submit Feedback
          </button>
        </div>
      </div>
    </BodyModal>
  );
};

TempoTaggedReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mealId: PropTypes.string.isRequired,
  mealSelectionId: PropTypes.string,
  menuId: PropTypes.string.isRequired,
  existingReview: Types.review,
  onAddReview: PropTypes.func.isRequired,
  onAddReviewForCookbook: PropTypes.func.isRequired,
  onUpdateReview: PropTypes.func.isRequired,
  onUpdateReviewForCookbook: PropTypes.func.isRequired,
  photoSrc: PropTypes.string.isRequired,
  onShowToast: PropTypes.func,
  title: PropTypes.string.isRequired,
  reviewReasons: PropTypes.arrayOf(PropTypes.string),
  ratingSubmittedEvent: PropTypes.string,
  reviewSubmittedEvent: PropTypes.string,
};

const mapDispatchToProps = {
  onAddReview: addReview,
  onAddReviewForCookbook: addReviewForCookbook,
  onUpdateReview: updateReview,
  onUpdateReviewForCookbook: updateReviewForCookbook,
};

const mapStateToProps = state => {
  const reviewReasons = fromDashboard.selectReviewReasonList(state);

  return {
    reviewReasons,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TempoTaggedReviewModal);
